import PageWithBreadcrumbs from '@/components/page-with-breadcrumbs';
import { Spinner } from '@/components/ui/spinner';
import useStepperState from '@/hooks/useStepperState';
import { fetchClient } from '@/lib/api';
import { lerPermissoes } from '@/lib/auth';
import { useTranslation } from '@/lib/i18n';
import { enabledPageGuard } from '@/lib/migration';
import { permissaoGuard } from '@/lib/permissions';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import BaseRegistroStepper from './-tipos/base/base-stepper';
import RegistroDesktopStepper from './-tipos/desktop';
import RegistroSecullumRhStepper from './-tipos/secullum-rh';
import { registrosContext, useRegistros } from './-utils/context';
import type { TipoRegistro } from './-utils/tipos';

export const Route = createFileRoute(
  '/__authenticated/comercial/produto/registros/',
)({
  beforeLoad: async () => {
    await enabledPageGuard('registros', {
      redirectToOldSitePage: 'comercial/produto/registros',
    });

    await permissaoGuard('Registros');
  },
  loader: async () => {
    const { data } = await fetchClient.GET('/revendas/me/bloqueio-registros');

    return { bloqueado: data?.bloqueado ?? false };
  },
  pendingComponent: () => (
    <div className="flex flex-1 items-center justify-center py-20">
      <Spinner className="size-6" />
    </div>
  ),
  component: RouteComponent,
});

function RouteComponent() {
  const [tipo, setTipo] = useState<TipoRegistro | null>(buscarTipoInicial);
  const [reinicios, setReinicios] = useState(0);

  const stepperState = useStepperState();

  const data = Route.useLoaderData();

  const { t } = useTranslation('private');

  function buscarTipoInicial() {
    const permissoes = lerPermissoes();

    if (permissoes.includes('RegistrarSistemasWeb')) {
      return 'secullum-rh';
    }

    return permissoes.includes('RegistrarSistemasDesktop') ? 'desktop' : null;
  }

  // Remonta o stepper para zerar formulários e resultados. O tipo selecionado é mantido de propósito.
  function reiniciar() {
    stepperState.reset();
    setReinicios(anterior => anterior + 1);
  }

  function handleTipoChange(novoTipo: TipoRegistro) {
    reiniciar();
    setTipo(novoTipo);
  }

  return (
    <PageWithBreadcrumbs
      breadcrumbs={[
        t($ => $.navbar.comercial),
        t($ => $.navbar.produto),
        t($ => $.navbar.registros),
      ]}
    >
      <registrosContext.Provider
        value={{
          bloqueado: data.bloqueado,
          tipo,
          handleTipoChange,
          stepperState,
          reiniciar,
        }}
      >
        <StepperContainer key={reinicios} />
      </registrosContext.Provider>
    </PageWithBreadcrumbs>
  );
}

function StepperContainer() {
  const { tipo } = useRegistros();

  if (tipo === null) {
    return <BaseRegistroStepper />;
  }

  return tipo === 'desktop' ? (
    <RegistroDesktopStepper />
  ) : (
    <RegistroSecullumRhStepper />
  );
}
